import React, { useEffect, useState, useCallback } from "react";
import { Search, Star, AlertCircle, FileText, Download, Plus, Megaphone, HelpCircle, FolderOpen, Archive, Eye, EyeOff, Trash2, History, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

function Card({ children, className = "", ...rest }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ${className}`} {...rest}>{children}</div>;
}

const TABS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "library", label: "Resource Library" },
  { key: "faqs", label: "FAQs" },
  { key: "announcements", label: "Announcements" },
];

export default function SupervisorHub() {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const isAdmin = user?.role === "administrator" || user?.role === "admin";

  const [tab, setTab] = useState("dashboard");
  const [dash, setDash] = useState(null);
  const [categories, setCategories] = useState([]);
  const [resources, setResources] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [sort, setSort] = useState("newest");

  const [addRes, setAddRes] = useState(false);
  const [addFaq, setAddFaq] = useState(false);
  const [addAnn, setAddAnn] = useState(false);
  const [addCat, setAddCat] = useState(false);

  const loadDash = useCallback(() => api.get("/hub/dashboard").then((r) => setDash(r.data)).catch(() => {}), []);
  const loadCats = useCallback(() => api.get("/hub/categories").then((r) => setCategories(r.data)).catch(() => {}), []);
  const loadRes = useCallback(() => {
    const p = new URLSearchParams();
    if (filterCat) p.set("category_id", filterCat);
    if (search) p.set("q", search);
    p.set("sort", sort);
    return api.get(`/hub/resources?${p}`).then((r) => setResources(r.data)).catch(() => {});
  }, [filterCat, search, sort]);
  const loadFaqs = useCallback(() => api.get(`/hub/faqs${search ? `?q=${encodeURIComponent(search)}` : ""}`).then((r) => setFaqs(r.data)).catch(() => {}), [search]);
  const loadAnns = useCallback(() => api.get("/hub/announcements").then((r) => setAnnouncements(r.data)).catch(() => {}), []);

  useEffect(() => { loadDash(); loadCats(); }, [loadDash, loadCats]);
  useEffect(() => { if (tab === "library") loadRes(); if (tab === "faqs") loadFaqs(); if (tab === "announcements") loadAnns(); }, [tab, loadRes, loadFaqs, loadAnns]);

  const catName = (id) => categories.find((c) => c.id === id)?.name || "Uncategorized";

  const download = async (r) => {
    try {
      const res = await api.get(`/hub/resources/${r.id}/download`);
      window.open(res.data.url, "_blank");
    } catch (e) { toast({ title: "Unable to open", description: e.response?.data?.detail || "", variant: "destructive" }); }
  };

  const toggle = async (r, field) => {
    try { await api.put(`/admin/hub/resources/${r.id}`, { [field]: !r[field] }); toast({ title: "Updated" }); loadRes(); loadDash(); }
    catch (e) { toast({ title: "Failed", description: e.response?.data?.detail || "", variant: "destructive" }); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete "${r.title}"?`)) return;
    try { await api.delete(`/admin/hub/resources/${r.id}`); toast({ title: "Deleted" }); loadRes(); loadDash(); }
    catch (e) { toast({ title: "Failed", description: e.response?.data?.detail || "", variant: "destructive" }); }
  };

  const removeItem = async (kind, id, reload) => {
    if (!window.confirm("Delete this item?")) return;
    try { await api.delete(`/admin/hub/${kind}/${id}`); toast({ title: "Deleted" }); reload(); }
    catch (e) { toast({ title: "Failed", description: e.response?.data?.detail || "", variant: "destructive" }); }
  };

  const ResourceRow = ({ r }) => (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-50 py-3" data-testid={`hub-resource-${r.id}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 shrink-0 text-violet-500" />
          <span className="font-medium text-slate-800">{r.title}</span>
          {r.is_important && <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-600">Important</span>}
          {r.is_featured && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-600">Featured</span>}
          {r.is_current && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Current v{r.version}</span>}
          {r.is_archived && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">Archived</span>}
          {isAdmin && !r.is_published && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">Unpublished</span>}
        </div>
        <div className="mt-0.5 text-xs text-slate-400">{catName(r.category_id)}{r.effective_date ? ` · effective ${r.effective_date}` : ""}{r.description ? ` · ${r.description}` : ""}</div>
      </div>
      <div className="flex items-center gap-1">
        <Button size="sm" variant="outline" onClick={() => download(r)} data-testid={`hub-download-${r.id}`}><Download className="h-3.5 w-3.5" /></Button>
        {isAdmin && (
          <>
            <Button size="sm" variant="ghost" title="Edit / replace file" onClick={() => setEditRes(r)} data-testid={`hub-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
            <Button size="sm" variant="ghost" title="Feature" onClick={() => toggle(r, "is_featured")} data-testid={`hub-feature-${r.id}`}><Star className={`h-3.5 w-3.5 ${r.is_featured ? "text-amber-500" : ""}`} /></Button>
            <Button size="sm" variant="ghost" title="Important" onClick={() => toggle(r, "is_important")} data-testid={`hub-important-${r.id}`}><AlertCircle className={`h-3.5 w-3.5 ${r.is_important ? "text-rose-500" : ""}`} /></Button>
            <Button size="sm" variant="ghost" title={r.is_published ? "Unpublish" : "Publish"} onClick={() => toggle(r, "is_published")} data-testid={`hub-publish-${r.id}`}>{r.is_published ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}</Button>
            <Button size="sm" variant="ghost" title={r.is_archived ? "Restore" : "Archive"} onClick={() => toggle(r, "is_archived")} data-testid={`hub-archive-${r.id}`}><Archive className={`h-3.5 w-3.5 ${r.is_archived ? "text-violet-500" : ""}`} /></Button>
            <Button size="sm" variant="ghost" title="Delete" onClick={() => del(r)} data-testid={`hub-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-500" /></Button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="supervisor-hub">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Supervisor Hub</h1>
        <p className="text-sm text-slate-500">Everything you need to support your contractors.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { if (tab === "faqs") loadFaqs(); else { setTab("library"); loadRes(); } } }} placeholder="Search the Supervisor Hub…" className="pl-9" data-testid="hub-search" />
        </div>
        <Button variant="outline" onClick={() => navigate("/supervisor/applicants")} data-testid="hub-my-contractors"><Users className="mr-1.5 h-4 w-4" /> My Contractors</Button>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`hub-tab-${t.key}`}
            className={`px-4 py-2 text-sm font-semibold transition-colors ${tab === t.key ? "border-b-2 border-violet-600 text-violet-700" : "text-slate-500 hover:text-slate-700"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {isAdmin && (
        <div className="flex flex-wrap gap-2" data-testid="hub-admin-actions">
          <Button size="sm" onClick={() => setAddRes(true)} className="btn-primary-gradient border-0 text-white" data-testid="add-resource-btn"><Plus className="mr-1 h-4 w-4" /> Add Resource</Button>
          <Button size="sm" variant="outline" onClick={() => setAddCat(true)} data-testid="add-category-btn"><Plus className="mr-1 h-4 w-4" /> Add Category</Button>
          <Button size="sm" variant="outline" onClick={() => setAddFaq(true)} data-testid="add-faq-btn"><Plus className="mr-1 h-4 w-4" /> Add FAQ</Button>
          <Button size="sm" variant="outline" onClick={() => setAddAnn(true)} data-testid="add-announcement-btn"><Plus className="mr-1 h-4 w-4" /> Add Announcement</Button>
        </div>
      )}

      {tab === "dashboard" && dash && (
        <div className="space-y-5">
          {dash.important_announcements?.length > 0 && (
            <Card className="border-rose-200 bg-rose-50/50" data-testid="hub-important-announcements">
              <div className="mb-2 flex items-center gap-2 text-sm font-bold text-rose-700"><Megaphone className="h-4 w-4" /> Important Announcements</div>
              {dash.important_announcements.map((a) => (
                <div key={a.id} className="border-t border-rose-100 py-2 first:border-0">
                  <div className="font-semibold text-slate-900">{a.title}</div>
                  <div className="text-sm text-slate-600">{a.message}</div>
                  <div className="text-xs text-slate-400">{a.published_by_name} · {new Date(a.published_at).toLocaleDateString()}</div>
                </div>
              ))}
            </Card>
          )}
          {(dash.important?.length > 0 || dash.featured?.length > 0) && (
            <Card data-testid="hub-featured">
              <div className="mb-2 text-sm font-bold text-slate-900">Important &amp; Featured</div>
              {[...dash.important, ...dash.featured.filter((f) => !dash.important.find((i) => i.id === f.id))].map((r) => <ResourceRow key={r.id} r={r} />)}
            </Card>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {dash.categories.map((c) => (
              <Card key={c.id} className="cursor-pointer transition-shadow hover:shadow-md" data-testid={`hub-category-${c.id}`}
                onClick={() => { setFilterCat(c.id); setTab("library"); }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><FolderOpen className="h-4 w-4 text-violet-500" /><span className="font-semibold text-slate-800">{c.name}</span></div>
                  <span className="text-sm font-bold text-slate-400">{c.resource_count}</span>
                </div>
              </Card>
            ))}
          </div>
          {dash.recent?.length > 0 && (
            <Card data-testid="hub-recent">
              <div className="mb-2 text-sm font-bold text-slate-900">Recently Added</div>
              {dash.recent.map((r) => <ResourceRow key={r.id} r={r} />)}
            </Card>
          )}
        </div>
      )}

      {tab === "library" && (
        <Card>
          <div className="mb-3 flex flex-wrap gap-2">
            <select className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm" value={filterCat} onChange={(e) => setFilterCat(e.target.value)} data-testid="hub-filter-category">
              <option value="">All categories</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm" value={sort} onChange={(e) => setSort(e.target.value)} data-testid="hub-sort">
              <option value="newest">Newest</option>
              <option value="title">Title A–Z</option>
            </select>
            <Button size="sm" variant="outline" onClick={loadRes}>Apply</Button>
          </div>
          {resources.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">No resources found.</p> : resources.map((r) => <ResourceRow key={r.id} r={r} />)}
        </Card>
      )}

      {tab === "faqs" && (
        <Card data-testid="hub-faqs">
          {faqs.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">No FAQs yet.</p> : faqs.map((f) => (
            <details key={f.id} className="border-b border-slate-50 py-3" data-testid={`hub-faq-${f.id}`}>
              <summary className="flex cursor-pointer items-center gap-2 font-medium text-slate-800"><HelpCircle className="h-4 w-4 text-violet-500" /> {f.question}
                {isAdmin && <button onClick={(e) => { e.preventDefault(); removeItem("faqs", f.id, loadFaqs); }} className="ml-auto text-rose-400 hover:text-rose-600" data-testid={`hub-faq-delete-${f.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
              </summary>
              <div className="mt-2 pl-6 text-sm text-slate-600">{f.answer}</div>
            </details>
          ))}
        </Card>
      )}

      {tab === "announcements" && (
        <Card data-testid="hub-announcements-list">
          {announcements.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">No announcements.</p> : announcements.map((a) => (
            <div key={a.id} className="border-b border-slate-50 py-3" data-testid={`hub-announcement-${a.id}`}>
              <div className="flex items-center gap-2"><span className="font-semibold text-slate-900">{a.title}</span>{a.is_important && <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-600">Important</span>}
                {isAdmin && <button onClick={() => removeItem("announcements", a.id, () => { loadAnns(); loadDash(); })} className="ml-auto text-rose-400 hover:text-rose-600" data-testid={`hub-ann-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
              </div>
              <div className="text-sm text-slate-600">{a.message}</div>
              <div className="text-xs text-slate-400">{a.published_by_name} · {new Date(a.published_at).toLocaleString()}</div>
            </div>
          ))}
        </Card>
      )}

      <AddResourceDialog open={addRes} onClose={() => setAddRes(false)} categories={categories} onDone={() => { loadRes(); loadDash(); }} />
      <AddCategoryDialog open={addCat} onClose={() => setAddCat(false)} onDone={() => { loadCats(); loadDash(); }} />
      <AddFaqDialog open={addFaq} onClose={() => setAddFaq(false)} categories={categories} onDone={() => loadFaqs()} />
      <AddAnnouncementDialog open={addAnn} onClose={() => setAddAnn(false)} onDone={() => { loadAnns(); loadDash(); }} />
      <HubEditResourceDialog resource={editRes} categories={categories} onClose={() => setEditRes(null)} onDone={() => { loadRes(); loadDash(); }} />
    </div>
  );
}

function AddResourceDialog({ open, onClose, categories, onDone }) {
  const { toast } = useToast();
  const [f, setF] = useState({ title: "", description: "", category_id: "", version: "1.0", effective_date: "", is_important: false, is_featured: false, is_published: true, external_url: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("file", file);
      await api.post("/admin/hub/resources", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast({ title: "Resource added" });
      onClose(); onDone();
      setF({ title: "", description: "", category_id: "", version: "1.0", effective_date: "", is_important: false, is_featured: false, is_published: true, external_url: "" }); setFile(null);
    } catch (e) { toast({ title: "Failed", description: e.response?.data?.detail || "", variant: "destructive" }); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-auto">
        <DialogHeader><DialogTitle>Add Supervisor Resource</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Title</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="res-title" /></div>
          <div className="space-y-1.5"><Label>Description</Label><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="res-desc" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Category</Label>
              <select className="w-full rounded-lg border border-slate-200 px-2 py-2 text-sm" value={f.category_id} onChange={(e) => setF({ ...f, category_id: e.target.value })} data-testid="res-category">
                <option value="">Select…</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5"><Label>Version</Label><Input value={f.version} onChange={(e) => setF({ ...f, version: e.target.value })} data-testid="res-version" /></div>
          </div>
          <div className="space-y-1.5"><Label>Effective date</Label><Input type="date" value={f.effective_date} onChange={(e) => setF({ ...f, effective_date: e.target.value })} data-testid="res-effective" /></div>
          <div className="space-y-1.5"><Label>File</Label><Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="res-file" /></div>
          <div className="space-y-1.5"><Label>…or external link</Label><Input value={f.external_url} onChange={(e) => setF({ ...f, external_url: e.target.value })} placeholder="https://…" data-testid="res-url" /></div>
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.is_important} onChange={(e) => setF({ ...f, is_important: e.target.checked })} data-testid="res-important" /> Important</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.is_featured} onChange={(e) => setF({ ...f, is_featured: e.target.checked })} data-testid="res-featured" /> Featured</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.is_published} onChange={(e) => setF({ ...f, is_published: e.target.checked })} data-testid="res-published" /> Published</label>
          </div>
        </div>
        <DialogFooter><Button disabled={!f.title || saving} onClick={save} className="btn-primary-gradient border-0 text-white" data-testid="save-resource-btn">{saving ? "Uploading…" : "Add resource"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddCategoryDialog({ open, onClose, onDone }) {
  const { toast } = useToast();
  const [name, setName] = useState("");
  const save = async () => {
    try { await api.post("/admin/hub/categories", { name }); toast({ title: "Category added" }); setName(""); onClose(); onDone(); }
    catch (e) { toast({ title: "Failed", description: e.response?.data?.detail || "", variant: "destructive" }); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Add Category</DialogTitle></DialogHeader>
        <div className="space-y-1.5"><Label>Category name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="cat-name" /></div>
        <DialogFooter><Button disabled={!name} onClick={save} className="btn-primary-gradient border-0 text-white" data-testid="save-category-btn">Add</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddFaqDialog({ open, onClose, categories, onDone }) {
  const { toast } = useToast();
  const [f, setF] = useState({ question: "", answer: "", category_id: "", is_published: true });
  const save = async () => {
    try { await api.post("/admin/hub/faqs", f); toast({ title: "FAQ added" }); setF({ question: "", answer: "", category_id: "", is_published: true }); onClose(); onDone(); }
    catch (e) { toast({ title: "Failed", description: e.response?.data?.detail || "", variant: "destructive" }); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Add FAQ</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Question</Label><Input value={f.question} onChange={(e) => setF({ ...f, question: e.target.value })} data-testid="faq-question" /></div>
          <div className="space-y-1.5"><Label>Answer</Label><textarea className="w-full rounded-lg border border-slate-200 p-2 text-sm" rows={4} value={f.answer} onChange={(e) => setF({ ...f, answer: e.target.value })} data-testid="faq-answer" /></div>
        </div>
        <DialogFooter><Button disabled={!f.question} onClick={save} className="btn-primary-gradient border-0 text-white" data-testid="save-faq-btn">Add FAQ</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddAnnouncementDialog({ open, onClose, onDone }) {
  const { toast } = useToast();
  const [f, setF] = useState({ title: "", message: "", priority: "Normal", is_important: false, is_published: true });
  const save = async () => {
    try { await api.post("/admin/hub/announcements", f); toast({ title: "Announcement posted" }); setF({ title: "", message: "", priority: "Normal", is_important: false, is_published: true }); onClose(); onDone(); }
    catch (e) { toast({ title: "Failed", description: e.response?.data?.detail || "", variant: "destructive" }); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Add Announcement</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Title</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="ann-title" /></div>
          <div className="space-y-1.5"><Label>Message</Label><textarea className="w-full rounded-lg border border-slate-200 p-2 text-sm" rows={3} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="ann-message" /></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.is_important} onChange={(e) => setF({ ...f, is_important: e.target.checked })} data-testid="ann-important" /> Mark as Important</label>
        </div>
        <DialogFooter><Button disabled={!f.title} onClick={save} className="btn-primary-gradient border-0 text-white" data-testid="save-announcement-btn">Post</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
